/** Plain-text preview helpers for scroll / note HTML (shared by home strips + cards). */

type BlockNoteInline = { type?: string; text?: string; content?: BlockNoteInline[] };
type BlockNoteBlock = {
  type?: string;
  content?: BlockNoteInline[] | string;
  children?: BlockNoteBlock[];
};

function extractInlineText(items: BlockNoteInline[] = []): string {
  return items
    .map((item) => {
      if (typeof item?.text === "string") return item.text;
      if (Array.isArray(item?.content)) return extractInlineText(item.content);
      return "";
    })
    .join(" ");
}

export function extractBlockNoteText(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as BlockNoteBlock[];
    if (!Array.isArray(parsed)) return "";
    const acc: string[] = [];

    const visit = (block: BlockNoteBlock) => {
      if (Array.isArray(block.content)) {
        const t = extractInlineText(block.content).trim();
        if (t) acc.push(t);
      } else if (typeof block.content === "string") {
        const t = block.content.trim();
        if (t) acc.push(t);
      }
      if (Array.isArray(block.children)) {
        for (const child of block.children) visit(child);
      }
    };

    for (const block of parsed) visit(block);
    return acc.join(" ").replace(/\s+/g, " ").trim();
  } catch {
    return "";
  }
}

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"');
}

export function stripHtmlTags(html: string): string {
  return decodeHtmlEntities(
    html
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/?(p|div|h[1-6]|blockquote|span)[^>]*>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim(),
  );
}

function tryParseJsonPreview(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return "";
  return extractBlockNoteText(trimmed) || "";
}

function extractHeroField(html: string, attr: string): string {
  const openRe = new RegExp(`<p[^>]*data-hero-${attr}[^>]*>`, "i");
  const openMatch = openRe.exec(html);
  if (!openMatch) return "";
  const start = openMatch.index + openMatch[0].length;
  const rest = html.slice(start);
  const nextField = rest.search(/<p[^>]*data-hero-/i);
  const endDiv = rest.search(/<\/div>/i);
  let end = rest.length;
  if (nextField >= 0) end = Math.min(end, nextField);
  if (endDiv >= 0) end = Math.min(end, endDiv);
  return stripHtmlTags(rest.slice(0, end));
}

function extractHeroFromHtml(html: string): {
  eyebrow: string;
  title: string;
  subtitle: string;
} | null {
  const heroMatch = html.match(/data-inora-preview=["']hero["']/i);
  if (!heroMatch) return null;

  const e = extractHeroField(html, "eyebrow");
  const t = extractHeroField(html, "title");
  const s = extractHeroField(html, "subtitle");

  if (!e && !t && !s) return null;
  return {
    eyebrow: stripHtmlTags(e),
    title: stripHtmlTags(t),
    subtitle: stripHtmlTags(s),
  };
}

export type CardPreview =
  | {
      kind: "hero";
      eyebrow: string;
      title: string;
      subtitle: string;
      lines: string[];
    }
  | { kind: "text"; body: string; bullets: string[] }
  | { kind: "empty" };

function extractPreviewLinesFromHtml(html: string): string[] {
  const matches = [
    ...html.matchAll(
      /<p[^>]*data-inora-preview=["']line["'][^>]*>([\s\S]*?)<\/p>/gi,
    ),
  ];
  return matches
    .map((m) => stripHtmlTags(m[1] ?? ""))
    .filter(Boolean)
    .slice(0, 3);
}

export function getCardPreview(
  html: string | null | undefined,
  maxBodyLen = 180,
): CardPreview {
  if (!html?.trim()) return { kind: "empty" };

  const trimmed = html.trim();

  const hero = extractHeroFromHtml(html);
  if (hero) {
    const lines = extractPreviewLinesFromHtml(html);
    const subtitle = hero.subtitle;
    const filteredLines = lines.filter(
      (line) => line !== subtitle && line !== hero.title && line !== hero.eyebrow,
    );
    return { kind: "hero", ...hero, lines: filteredLines };
  }

  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    const fromJson = tryParseJsonPreview(trimmed);
    if (fromJson) {
      return {
        kind: "text",
        body:
          fromJson.length > maxBodyLen
            ? `${fromJson.slice(0, maxBodyLen)}…`
            : fromJson,
        bullets: [],
      };
    }
    return { kind: "empty" };
  }

  const jsonInTag = trimmed.match(/<[^>]+>\s*(\[[\s\S]*\]|\{[\s\S]*\})\s*<\/[^>]+>/);
  if (jsonInTag?.[1]) {
    const fromJson = tryParseJsonPreview(jsonInTag[1]);
    if (fromJson) {
      return {
        kind: "text",
        body:
          fromJson.length > maxBodyLen
            ? `${fromJson.slice(0, maxBodyLen)}…`
            : fromJson,
        bullets: [],
      };
    }
  }

  const bulletMatches = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
  const bullets = bulletMatches
    .map((m) => stripHtmlTags(m[1] ?? ""))
    .filter(Boolean)
    .slice(0, 5);

  const bodyHtml = html
    .replace(/<ul[^>]*>[\s\S]*?<\/ul>/gi, "")
    .replace(/<ol[^>]*>[\s\S]*?<\/ol>/gi, "");
  const body = stripHtmlTags(bodyHtml);

  if (!body && bullets.length === 0) return { kind: "empty" };

  return {
    kind: "text",
    body: body.length > maxBodyLen ? `${body.slice(0, maxBodyLen)}…` : body,
    bullets,
  };
}

/** @deprecated Prefer getCardPreview — kept for existing callers */
export function getCardContent(html: string | null | undefined, maxBodyLen = 180) {
  const preview = getCardPreview(html, maxBodyLen);
  if (preview.kind === "hero") {
    const parts = [preview.eyebrow, preview.title, preview.subtitle].filter(Boolean);
    return { body: parts.join(" · "), bullets: [] as string[] };
  }
  if (preview.kind === "text") {
    return { body: preview.body, bullets: preview.bullets };
  }
  return { body: "", bullets: [] as string[] };
}

export function scrollHasMeaningfulContent(
  html: string | null | undefined,
  css?: string | null,
  js?: string | null,
) {
  const preview = getCardPreview(html ?? "", 4000);
  if (preview.kind === "hero") {
    return !!(preview.title || preview.subtitle || preview.eyebrow);
  }
  if (preview.kind === "text") {
    if (preview.bullets.length > 0) return true;
    if (preview.body.trim().length > 0) return true;
  }
  return !!(css?.trim() || js?.trim());
}

/** Strip BlockNote JSON / sync preview markup from notebook paragraph content. */
export function sanitizeRichTextContent(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    const extracted = extractBlockNoteText(trimmed);
    return extracted || "";
  }

  if (/data-inora-preview|data-hero-/i.test(trimmed)) {
    return stripHtmlTags(trimmed);
  }

  return raw;
}

/** Plain text for heading/quote richtext rows — strips accidental `<p>` wrappers. */
export function sanitizePlainTextNodeContent(raw: string): string {
  const base = sanitizeRichTextContent(raw);
  const trimmed = base.trim();
  if (!trimmed) return "";
  if (trimmed.includes("<")) return stripHtmlTags(trimmed);
  return base;
}

export type RichTextBlockSeed = {
  id: string;
  type: "p" | "h3" | "blockquote";
  content: string;
};

/** Convert legacy `{ content: string }` scroll payloads into notebook richtext blocks. */
export function legacyContentToRichTextBlocks(
  raw: string,
  newId: () => string = () => crypto.randomUUID(),
): RichTextBlockSeed[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    const text = extractBlockNoteText(trimmed);
    if (!text) return [];
    const parts = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    const chunks = parts.length > 0 ? parts : [text];
    return chunks.map((content) => ({
      id: newId(),
      type: "p" as const,
      content,
    }));
  }

  if (/data-inora-preview|data-hero-/i.test(trimmed)) {
    const text = stripHtmlTags(trimmed);
    if (!text) return [];
    return [{ id: newId(), type: "p", content: text }];
  }

  if (trimmed.startsWith("<")) {
    return [{ id: newId(), type: "p", content: trimmed }];
  }

  return [{ id: newId(), type: "p", content: trimmed }];
}

export function escapeHtmlText(text: string) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
